import { createColumnHelper, type SortingFn } from '@tanstack/react-table';
import moment from 'moment';
import type { Transaction } from './types';

export const COLUMN_DEFS: { key: keyof Transaction; label: string }[] = [
  { key: 'order_number', label: 'Order #' },
  { key: 'customer_id', label: 'Customer ID' },
  { key: 'customer_name', label: 'Customer Name' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'subscriber_id', label: 'Subscriber ID' },
  { key: 'po_number', label: 'PO #' },
  { key: 'invoiced_on', label: 'Invoiced On' },
  { key: 'seat_count', label: 'Seats' },
  { key: 'item', label: 'Item' },
  { key: 'program_code', label: 'Program' },
  { key: 'seat_type', label: 'Seat Type' },
  { key: 'amount', label: 'Amount' },
  { key: 'admin_email', label: 'Admin Email' },
  { key: 'order_item_key', label: 'Order Item Key' },
  { key: 'ship_to_name', label: 'Ship To' },
  { key: 'netsuite_internal_id', label: 'NS Internal ID' },
];

const columnHelper = createColumnHelper<Transaction>();

const numericSortingFn: SortingFn<Transaction> = (rowA, rowB, columnId) => {
  const a = Number(rowA.getValue(columnId)) || 0;
  const b = Number(rowB.getValue(columnId)) || 0;
  return a - b;
};

const dateSortingFn: SortingFn<Transaction> = (rowA, rowB, columnId) => {
  const a = new Date(rowA.getValue<string>(columnId) || '').getTime() || 0;
  const b = new Date(rowB.getValue<string>(columnId) || '').getTime() || 0;
  return a - b;
};

const formatDate = (value: string | null): string => {
  if (!value) return '';
  const parsed = moment(value);
  if (!parsed.isValid()) return value;
  return parsed.format('MMM D, YYYY');
};

const NUMERIC_COLUMNS: Set<keyof Transaction> = new Set([
  'subscriber_id', 'seat_count', 'amount', 'netsuite_internal_id',
]);
const DATE_COLUMNS: Set<keyof Transaction> = new Set(['invoiced_on']);

const columns = COLUMN_DEFS.map(({ key, label }) => {
  if (key === 'amount') {
    return columnHelper.accessor(key, {
      cell: (info) => {
        const val = info.getValue();
        return val != null ? `$${Number(val).toFixed(2)}` : '';
      },
      header: label,
      sortingFn: numericSortingFn,
    });
  }

  if (DATE_COLUMNS.has(key)) {
    return columnHelper.accessor(key, {
      cell: info => formatDate(info.getValue() as string | null),
      header: label,
      sortingFn: dateSortingFn,
    });
  }

  if (NUMERIC_COLUMNS.has(key)) {
    return columnHelper.accessor(key, {
      header: label,
      sortingFn: numericSortingFn,
    });
  }

  return columnHelper.accessor(key, { header: label });
});

export default columns;
